import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './BoardPreview.css';

// The board as it will be built: one painting per painter, a dashed gap for
// any spot that still needs a canvas, and dimension rulers along two edges.
//
// Drawn to one scale across every choice - up to a cap, so small boards
// don't blow up to fill the frame - which means switching to a bigger
// canvas visibly makes a bigger board. Cells are absolutely positioned and
// transition their position and size, so changing the shape or the number
// of painters rearranges the board instead of redrawing it.

const MAX_PX_PER_CM = 7;
// Space kept around the board for the rulers: the mount, the gap to the
// ruler, and the measurement itself.
const MARGIN = 58;
// How far the rulers stand off the edge of the mount.
const RULER_GAP = 20;
// Gap between canvases, as a share of one canvas.
const GAP = 0.06;
// The "you" label only goes on when a canvas is big enough to carry it.
const MIN_CELL_FOR_LABEL = 38;

function BoardPreview({
  cols,
  rows,
  numCanvases,
  sizeCm,
  paintings,
  youLabel,
  label,
}) {
  const stageRef = useRef(null);
  const [box, setBox] = useState(null);
  // Canvases fly in one after another on first view; after that, a new one
  // appears straight away instead of waiting its turn in the queue.
  const [settled, setSettled] = useState(false);

  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    const measure = () => setBox({ w: stage.clientWidth, h: stage.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(stage);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(true), 1400);
    return () => clearTimeout(timer);
  }, []);

  const widthCm = cols * sizeCm;
  const heightCm = rows * sizeCm;

  let board = null;
  if (box && box.w > 0 && box.h > 0) {
    const scale = Math.max(
      0,
      Math.min((box.w - MARGIN * 2) / widthCm, (box.h - MARGIN * 2) / heightCm, MAX_PX_PER_CM)
    );
    const boardW = widthCm * scale;
    const boardH = heightCm * scale;
    const left = (box.w - boardW) / 2;
    const top = (box.h - boardH) / 2;
    const pitch = sizeCm * scale;
    const gap = pitch * GAP;
    const cell = pitch - gap;
    // The wooden mount reaches past the canvases; the rulers clear it.
    const mount = Math.max(5, pitch * 0.14);
    const offset = mount + RULER_GAP;

    const cells = [];
    for (let i = 0; i < cols * rows; i += 1) {
      const x = (i % cols) * pitch + gap / 2;
      const y = Math.floor(i / cols) * pitch + gap / 2;
      const place = {
        transform: `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0)`,
        width: cell,
        height: cell,
      };
      const delay = { animationDelay: settled ? '0ms' : `${Math.min(i, 36) * 24}ms` };

      if (i < numCanvases) {
        const src = paintings.length > 0 ? paintings[i % paintings.length] : null;
        cells.push(
          <div key={`p${i}`} className="bp-cell bp-tile" style={place}>
            <div className="bp-cell-inner" style={delay}>
              {src && <img src={src} alt="" draggable="false" decoding="async" />}
              {i === 0 && youLabel && cell >= MIN_CELL_FOR_LABEL && (
                <span className="bp-you">{youLabel}</span>
              )}
            </div>
          </div>
        );
      } else {
        cells.push(
          <div key={`s${i - numCanvases}`} className="bp-cell bp-empty" style={place}>
            <div className="bp-cell-inner" style={delay}>
              <span className="bp-empty-mark">+</span>
            </div>
          </div>
        );
      }
    }

    board = (
      <>
        <div className="bp-ruler bp-ruler--x" style={{ left, top: top - offset, width: boardW }}>
          <span>{widthCm} cm</span>
        </div>
        <div className="bp-ruler bp-ruler--y" style={{ left: left - offset, top, height: boardH }}>
          <span>{heightCm} cm</span>
        </div>
        <div
          className="bp-board"
          style={{ left, top, width: boardW, height: boardH, '--mount': `${mount}px` }}
        >
          {cells}
        </div>
      </>
    );
  }

  return (
    <div className="bp" role="img" aria-label={label}>
      <div ref={stageRef} className="bp-stage">
        {board}
      </div>
    </div>
  );
}

export default BoardPreview;
